import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function PLReport() {
  const [loads, setLoads] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-delivery_date', 500),
      client.entities.Expense.list('-date', 500),
      client.entities.Settlement.list('-finalized_date', 500),
    ]).then(([l, e, s]) => { setLoads(l); setExpenses(e); setSettlements(s); setLoading(false); });
  }, []);

  const filteredLoads = loads.filter(l => inRange(l.delivery_date || l.pickup_date, start, end) && l.status !== 'Available');
  const filteredExpenses = expenses.filter(e => inRange(e.date, start, end));
  const filteredSettlements = settlements.filter(s => inRange(s.finalized_date || s.settlement_date, start, end) && s.is_finalized);

  const grossRevenue = filteredLoads.reduce((s, l) => s + (l.rate || 0), 0);
  const carrierTake = filteredSettlements.reduce((s, s2) => s + (s2.carrier_take_amount || 0) + (s2.dispatch_fee_amount || 0), 0);
  const driverPay = filteredSettlements.reduce((s, s2) => s + (s2.final_net_pay ?? s2.net_driver_pay ?? 0), 0);
  const fuelAdvances = filteredSettlements.reduce((s, s2) => s + (s2.fuel_advance_total || 0), 0);
  const totalExpenses = filteredExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const netProfit = grossRevenue - totalExpenses - (driverPay + carrierTake);
  const margin = grossRevenue > 0 ? ((netProfit / grossRevenue) * 100).toFixed(1) : '0.0';

  const rows = [
    { Period: `${start || '—'} to ${end || '—'}`, 'Gross Revenue': grossRevenue.toFixed(2), 'Carrier Take': carrierTake.toFixed(2), 'Driver Pay': driverPay.toFixed(2), 'Fuel Advances': fuelAdvances.toFixed(2), 'Expenses': totalExpenses.toFixed(2), 'Net Profit': netProfit.toFixed(2), 'Profit Margin %': margin }
  ];

  const summaryItems = [
    { label: 'Total Revenue', value: `$${grossRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'text-green-400' },
    { label: 'Total Expenses', value: `$${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'text-red-400' },
    { label: 'Net Profit', value: `$${netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: netProfit >= 0 ? 'text-green-400' : 'text-red-400' },
    { label: 'Avg Margin', value: `${margin}%`, color: 'text-primary' },
  ];

  const handlePrint = () => {
    const cols = ['Gross Revenue', 'Carrier Take', 'Driver Pay', 'Fuel Advances', 'Expenses', 'Net Profit', 'Margin %'];
    const dataRows = [[
      { value: `$${grossRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'positive' },
      { value: `-$${carrierTake.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'negative' },
      { value: `-$${driverPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'negative' },
      { value: `-$${fuelAdvances.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'negative' },
      { value: `-$${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'negative' },
      { value: `$${netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: netProfit >= 0 ? 'positive' : 'negative' },
      { value: `${margin}%`, class: 'gold' },
    ]];
    const cards = [
      { label: 'Total Revenue', value: `$${grossRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'gold' },
      { label: 'Total Expenses', value: `$${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'negative' },
      { label: 'Net Profit', value: `$${netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: netProfit >= 0 ? 'positive' : 'negative' },
      { label: 'Avg Margin', value: `${margin}%`, color: 'gold' },
    ];
    const html = generatePrintHTML('Profit & Loss Statement', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, null, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(rows, 'pl-report.csv')} exportDisabled={loading} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {summaryItems.map(i => (
          <div key={i.label} className="glass-card p-4 text-center">
            <p className={`text-lg font-space font-bold ${i.color}`}>{i.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{i.label}</p>
          </div>
        ))}
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Gross Revenue', 'Carrier Take', 'Driver Pay', 'Fuel Advances', 'Expenses', 'Net Profit', 'Margin %'].map(h => (
                <th key={h} className="text-right px-4 py-3 text-muted-foreground font-medium first:text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="hover:bg-white/3">
              <td className="px-4 py-3 text-right font-semibold text-green-400">${grossRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className="px-4 py-3 text-right text-red-400">−${carrierTake.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className="px-4 py-3 text-right text-red-400">−${driverPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className="px-4 py-3 text-right text-amber-400">−${fuelAdvances.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className="px-4 py-3 text-right text-red-400">−${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className={`px-4 py-3 text-right font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>${netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
              <td className="px-4 py-3 text-right text-primary font-semibold">{margin}%</td>
            </tr>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Revenue based on loads with delivery date in range. Expenses based on expense records. Driver pay based on finalized settlements.</p>
    </div>
  );
}